"use client";

import React, {useState} from "react";

import Image from "next/image";
import Chip from "@/components/global/ui/Chip";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";
import BackgroundVideo from "@/components/global/utils/BackgroundVideo";

import {PageLiveCallSchema} from "@/sanity/pages/PageLiveCallTemplate";

import bento1d from "@/public/live-call/bento1d.jpg";
import bento1m from "@/public/live-call/bento1m.jpg";
import bento2 from "@/public/live-call/bento2.svg";
import bento3 from "@/public/live-call/bento3-fallback.png";
import bento4 from "@/public/live-call/bento4-fallback.png";
import bento5d from "@/public/live-call/bento5d.jpg";
import bento5m from "@/public/live-call/bento5m.jpg";

export default function BentoSection({
  data,
  className
}: {
  data: PageLiveCallSchema;
  className?: string;
}) {
  return (
    <section className={cn(
      "relative overflow-clip pt-[12rem] lg:pt-[18rem]",
      className
    )}>
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="left"
      />
      <DecorationLine
        className="top-0"
        orientation="vertical"
        position="right"
      />
      <DecorationLine
        className="top-0 left-0 hidden lg:block"
        orientation="horizontal"
      />

      <div className="container relative flex flex-col items-center lg:pb-[5rem]">
        <Chip
          text={data?.bentoChip?.text}
          image={data?.bentoChip?.icon}
        />

        {data?.bentoText && (
          <div className="richtext">
            <PortableText
              value={data?.bentoText}
              components={richTextComponent}
            />
          </div>
        )}
      </div>

      <div className="relative pt-[3rem] lg:pt-[5rem]">
        <DecorationLine
          className="top-0 left-0 hidden lg:block"
          orientation="horizontal"
        />

        <div className="container flex flex-col items-center">
          <div className="w-[min(187.75rem,100%)] flex flex-col gap-[2rem] lg:gap-[1rem]">
            {/* bento row 1 */}
            <div className="flex flex-col lg:flex-row items-center lg:items-stretch gap-[2rem] lg:gap-[1rem]">
              {/* bento item 1 */}
              <div className="aspect-[398/490] lg:aspect-auto w-full sm:w-[55rem] lg:w-[min(105.5rem,100%)] flex-shrink-0 relative lg:min-h-[50rem] flex items-end justify-start p-[2.5rem] sm:p-[3rem] lg:p-[4rem]">
                <Image
                  className="absolute !w-full !h-full left-0 top-0 object-cover pointer-events-none hidden lg:block"
                  src={bento1d}
                  alt=""
                />
                <Image
                  className="absolute !w-full !h-full left-0 top-0 object-cover pointer-events-none lg:hidden"
                  src={bento1m}
                  alt=""
                />

                {data?.bentoItem1Text && (
                  <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2rem] lg:prose-p:text-[2.375rem] w-full lg:w-[min(51.375rem,100%)]">
                    <PortableText
                      value={data?.bentoItem1Text}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>

              {/* bento item 2 */}
              <div className="w-full sm:w-[55rem] lg:w-auto bg-[#112628] lg:grow relative lg:min-h-[50rem] flex flex-col gap-[5rem] lg:gap-[2rem] justify-between px-[2.5rem] pb-[2.5rem] pt-[1rem] lg:pt-0 sm:px-[3rem] sm:pb-[3rem] lg:px-[4rem] lg:pb-[4rem]">
                <Image
                  className="w-full"
                  src={bento2}
                  alt=""
                />

                {data?.bentoItem2Text && (
                  <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2rem] lg:prose-p:text-[2.375rem]">
                    <PortableText
                      value={data?.bentoItem2Text}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* bento row 2 grid */}
            <div className="flex flex-col lg:flex-row-reverse items-center lg:items-stretch gap-[2rem] lg:gap-[1rem]">
              {/* bento right side items */}
              <div className="flex flex-col items-center lg:items-stretch gap-[2rem] lg:gap-[1rem] lg:grow">
                {/* bento item 3 */}
                <div className="w-full sm:w-[55rem] lg:w-full aspect-square lg:aspect-auto bg-[#112628]/70 relative lg:min-h-[50rem] flex flex-col items-center justify-center">
                  <BackgroundVideo
                    playbackId="lABZ00oW1028HeCuL3HFGMnbJiPOun7TYYD9mCkvAZxKg"
                    fallbackImage={bento3}
                  />
                  <div className="absolute inset-0 bg-[#112628]/70"/>

                  {data?.bentoItem3Text && (
                    <div className="richtext prose-p:text-[6rem] lg:prose-p:text-[8rem] prose-p:leading-[.9em] prose-p:text-white/60 prose-p:!font-light prose-p:font-ivy prose-p:italic prose-strong:!font-normal prose-strong:text-white w-[min(43rem,100%)] lg:w-[min(60rem,100%)]">
                      <PortableText
                        value={data?.bentoItem3Text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>

                {/* bento item 4 */}
                <div className="aspect-[398/490] lg:aspect-auto w-full sm:w-[55rem] lg:w-full relative lg:min-h-[50rem] flex items-end justify-start p-[2.5rem] sm:p-[3rem] lg:p-[4rem]">
                  <Image
                    className="absolute !w-full !h-full left-0 top-0 object-cover pointer-events-none hidden lg:block"
                    src={bento5d}
                    alt=""
                  />
                  <Image
                    className="absolute !w-full !h-full left-0 top-0 object-cover pointer-events-none lg:hidden"
                    src={bento5m}
                    alt=""
                  />

                  {data?.bentoItem4Text && (
                    <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2rem] lg:prose-p:text-[2.375rem] w-full lg:w-[min(63.875rem,100%)]">
                      <PortableText
                        value={data?.bentoItem4Text}
                        components={richTextComponent}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* bento left side item */}
              {/* bento item 5 */}
              <div className="w-full sm:w-[55rem] lg:w-[67.875rem] flex-shrink-0 bg-[#112628] relative flex flex-col justify-between items-start gap-[3rem] p-[2.5rem] sm:p-[3rem] lg:p-[4rem]">
                <div className="w-full relative aspect-square mix-blend-screen">
                  <BackgroundVideo
                    className="[&_video]:object-top [&_img]:object-top"
                    playbackId="KQWqt435pz5Bvasz9vfyiVLQEEnYggooQtgLcL7at01g"
                    fallbackImage={bento4}
                  />
                </div>

                {data?.bentoItem5Text && (
                  <div className="richtext richtext-start prose-p:text-white/60 prose-strong:text-white prose-strong:font-normal prose-p:text-[2rem] lg:prose-p:text-[2.375rem] w-full lg:w-[min(46.5rem,100%)]">
                    <PortableText
                      value={data?.bentoItem5Text}
                      components={richTextComponent}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
